import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { OrgInfo } from '@app/account/account-api.model';
import { OrgViewBaseComponent } from '@app/orgs/components/org-view-base/org-view-base.component';
import { OrgPlansModalService } from '@app/orgs/services/org-plans-modal.service';
import {
  DataColumn,
  DataColumnDate,
  DataColumnList,
  MenuSettings,
} from '@app/shared/components/data-table/data-table';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { ModalService } from '@app/shared/services/modal.service';
import { translateWithDefaults } from '@app/shared/utils/i18n.utils';
import { resolveOrgScopedPlansAccess } from '@app/shared/utils/org-scoped-permissions.utils';
import { PlanPermissionsService } from '@app/target/services/plan-permissions.service';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import { TargetsService } from '@app/target/services/targets.service';
import { OrgTargetVM } from '@app/target/target-api.model';
import { RouterComponents } from '@degreed/lxp/org/models';
import { LDFlagsService, TrackerService } from '@degreed/lxp/services';
import { AuthService, AuthUser } from '@degreed/lxp/services/authorization';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, switchMap } from 'rxjs';
import { ConfirmMovePlanModalComponent } from '../confirm-move-plan-modal/confirm-move-plan-modal.component';
import { OrgPlansColumnIndex } from './enums/org-plans.enums';
import { OrgPlansFacade } from './rsm/org-plans.facade';
import { LoadOptions, OrgPlansViewModel } from './rsm/org-plans.model';

@Component({
  selector: 'dgx-org-plans',
  templateUrl: './org-plans.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OrgPlansComponent extends OrgViewBaseComponent implements OnInit {
  @ViewChild('titleColumn', { static: true })
  public titleColumnTemplate: TemplateRef<any>;
  @ViewChild('subtitleColumn', { static: true })
  public subtitleColumnTemplate: TemplateRef<any>;
  @ViewChild('visibilityColumn', { static: true })
  public visibilityColumnTemplate: TemplateRef<any>;
  @ViewChild('targetTypeColumn', { static: true })
  public targetTypeColumnTemplate: TemplateRef<any>;
  @ViewChild('authorsColumn', { static: true })
  public authorsColumnTemplate: TemplateRef<any>;
  @ViewChild('followersColumn', { static: true })
  public followersColumnTemplate: TemplateRef<any>;

  @Input() public actionOptionFiltered;

  public actions: MenuSettings;

  public authUser: AuthUser = this.authService.authUser;

  public columns: (DataColumn | DataColumnDate | DataColumnList)[];
  public enableCreatePlan: boolean;
  public canUpdatePlans: boolean;
  public canDeletePlans: boolean;
  public canAddOrgPlans: boolean;
  public hasFeaturedPlan: boolean;
  public orgInfo: OrgInfo;
  public vm$: Observable<OrgPlansViewModel>;

  /**
   * When ReBAC authorization enforcement is enabled for the org, the legacy
   * bulk "Edit Visibility" and "Add Collaborators" actions are replaced by the
   * ReBAC access model and must be hidden. Gated solely on the flag.
   */
  public readonly hideLegacyAccessActions: boolean =
    !!this.authUser.enableRebacAuthorizationEnforcement;

  public readonly CLASS_NAME = RouterComponents.ORG_PLANS;

  public i18n = this.translate.instant([
    'dgManagePathways_SearchPlaceholder',
    'OrgPlans_Types',
    'Core_AddToBundle',
    'Core_Delete',
    'Core_Confirm',
    'Core_UnfeatureResource',
    'Core_FeatureResource',
    'Core_MoveToGlobal',
    'Core_Clone',
    'Core_Edit',
    'Core_Add',
    'Core_Remove',
    'OrgPathwayManagerCtrl_DeletePromptDescription',
    'OrgPathwayManagerCtrl_AddCollaborators',
    'OrgPlans_OnFeaturedTooltip',
    'Core_ManageAccess',
  ]);

  /**
   * Explains WHY bulk Delete is disabled, so `aria-disabled` isn't announced
   * without a reason (WCAG 2.2 AA, 3.3.1). Shipped with an English default via
   * `translateWithDefaults` because the key is served from the backend resource
   * bundle — until the i18n team adds it, the default renders instead of the
   * raw key. Mirrors the org Pathways page.
   */
  public readonly bulkDeleteBlockedHelpText = translateWithDefaults(
    this.translate
  )(
    "One or more selected plans can't be deleted.",
    'OrgPlans_BulkDeleteBlocked'
  );
  /** Id linking {@link bulkDeleteBlockedHelpText} to the bulk Delete button. */
  public readonly bulkDeleteBlockedHelpId = 'plans-bulk-delete-blocked-help';

  private readonly deletePhrase = 'DELETE';
  private readonly deleteInstructions = this.translate.instant(
    'TargetCtrl_DeleteInstructions',
    { delete: this.deletePhrase }
  );

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private tracker: TrackerService,
    private translate: TranslateService,
    private targetsService: TargetsService,
    private sharedTargetService: SharedTargetService,
    private planPermissions: PlanPermissionsService,
    private orgPlansModalService: OrgPlansModalService,
    private facade: OrgPlansFacade,
    private ldFlagsService: LDFlagsService,
    private router: Router
  ) {
    super();
    this.vm$ = this.facade.vm$;
  }

  public ngOnInit(): void {
    this.orgInfo = this.authUser.orgInfo.find(
      (org) => org.organizationId === this.orgId
    );
    // ReBAC: `addPlansOrDirectories` (123) replaces `addOrganizationTarget`
    // (95). Resolved through the org-scoped helper so a ReBAC-OFF org still
    // reads the ROUTE org's own permission, not the default org's.
    const plansAccess = resolveOrgScopedPlansAccess(
      this.authUser,
      this.orgInfo
    );
    this.enableCreatePlan = plansAccess.canCreatePlans;

    // Page-level "is this user a plan author of any kind" gate — it decides
    // whether the update-oriented affordances render at all. Per-row edit
    // eligibility goes through `canEditPlan` below.
    this.canUpdatePlans = plansAccess.canUpdatePlans;

    // Page-level gate for the delete-oriented affordances (the bulk Delete
    // button and the delete half of row selection). Per-row and per-selection
    // eligibility go through `canDeletePlan` / the facade's bulk gate below.
    this.canDeletePlans = plansAccess.canDeletePlans;

    this.canAddOrgPlans = plansAccess.canCreatePlans;

    // Parity: the bulk-delete gate's ReBAC-OFF fallback must be the ROUTE org's
    // own delete permission — the same expression `canDeletePlan` passes — so a
    // multi-org admin keeps behaving exactly as before (PD-130899).
    this.facade.setLegacyCanDeletePlans(
      () => !!this.orgInfo?.permissions?.deleteOrganizationTarget
    );

    const dataSource = this.buildDataSource();
    this.facade.loadPlansFromSource(dataSource);

    this.targetsService
      .getBrowseTarget(this.authUser.defaultOrgId)
      .subscribe((data) => {
        this.hasFeaturedPlan = !!data?.targetId;
      });

    this.initActions();

    this.initializeColumns();

    this.tracker.trackEventData({
      action: 'Org Plans Page Clicked',
      properties: {
        ActionLocation: 'Manage Org Plans',
      },
    });
  }

  public showAddModal() {
    return this.orgPlansModalService
      .showAddModal(this.orgId, 'default')
      .subscribe();
  }

  public featurePlan(plan) {
    this.sharedTargetService
      .featureTarget(plan.targetId, true)
      .subscribe(() => {
        this.facade.togglePlanFeatured(plan, true);
      });
  }

  public unfeaturePlan(plan) {
    this.sharedTargetService
      .featureTarget(plan.targetId, false)
      .subscribe(() => {
        this.facade.togglePlanFeatured(plan, false);
      });
  }

  public moveToGlobal(plan) {
    this.modalService
      .show(ConfirmMovePlanModalComponent, {
        inputs: { plan },
      })
      .pipe(
        switchMap(() => {
          return this.sharedTargetService.moveTargetToChannel(plan.targetId);
        }),
        this.takeUntilDestroyed()
      )
      .subscribe(() => {
        // Visually remove plan
        this.facade.deletePlans([plan.id]);
      });
  }

  public clonePlan(plan: OrgTargetVM) {
    return this.orgPlansModalService.showCloneModal(plan);
  }

  public openEditModal(plan: OrgTargetVM) {
    return this.orgPlansModalService
      .showEditModal(plan, 'default')
      .subscribe(() => {
        this.facade.reset();
      });
  }

  /**
   * showDeleteModal
   * show and handle submit of deletion confirmation
   */
  public deletePlan(plan: OrgTargetVM) {
    return this.orgPlansModalService.showDeleteModal(plan).subscribe(() => {
      this.facade.deletePlans([plan.id]);
    });
  }

  /**
   * showBulkDeleteModal
   * show and handle submit of bulk deletion confirmation
   *
   * The button's disabled state is a UI affordance, not an authorization
   * boundary — this handler is reachable independently of it, so it re-checks
   * the per-item gate and refuses (PD-130899).
   */
  public showBulkDeleteModal() {
    const { selectedPlans, canBulkDeleteSelected } = this.facade.snapshot;
    if (!canBulkDeleteSelected) {
      return;
    }
    return this.orgPlansModalService
      .showBulkDeleteModal(selectedPlans.map((x) => x.targetId))
      .subscribe(() => {
        this.facade.deletePlans(selectedPlans.map((x) => x.id));
      });
  }

  /**
   * showBulkCollaboratorsModal
   * show and handle results of bulk collaborator add modal
   */
  public showBulkCollaboratorsModal() {
    return this.orgPlansModalService
      .showBulkCollaboratorsModal(
        this.facade.snapshot.selectedPlans.map((x) => x.targetId)
      )
      .subscribe((collaborators) => {
        this.facade.updateAuthors(
          this.facade.snapshot.selectedPlans,
          collaborators
        );
        this.clearSelections();
      });
  }

  /**
   * showBulkVisibilityModal
   * show and handle results of bulk visibility modal
   */
  public showBulkVisibilityModal() {
    return this.orgPlansModalService
      .showBulkVisibilityModal(
        this.facade.snapshot.selectedPlans.map((x) => x.targetId)
      )
      .subscribe((visibility) => {
        this.facade.updateVisibility(
          this.facade.snapshot.selectedPlans,
          visibility
        );
        this.clearSelections();
      });
  }

  public bulkSelect($event: { items: OrgTargetVM[]; isSelected: boolean }) {
    this.facade.selectPlans(
      $event.items.filter((x) => x.targetType !== 'Browse'),
      $event.isSelected
    );
  }

  public selectPlan($event: { item: OrgTargetVM; isSelected: boolean }) {
    this.facade.selectPlans([$event.item], $event.isSelected);
  }

  public clearSelections() {
    this.facade.selectPlans(this.facade.snapshot.selectedPlans, false);
  }

  /**
   * Whether the current user can edit the given plan row.
   *
   * Delegates to {@link PlanPermissionsService.canEdit}: ReBAC wins when
   * enforcement is enabled and the backend has hydrated the per-row `canEdit`
   * permission; otherwise the legacy org-level `updateOrganizationTarget`
   * permission is used via the supplied fallback thunk.
   *
   * ⚠️ The thunk deliberately reads the LEGACY route-org permission
   * (PD-130899). It only runs when ReBAC enforcement is OFF (or the backend
   * hasn't hydrated the row), and it must reproduce the pre-migration rule
   * exactly — that is the parity mechanism. Do not repoint it at the mapper.
   */
  private canEditPlan(plan: OrgTargetVM): boolean {
    return this.planPermissions.canEdit(
      { targetId: plan.targetId, permissions: plan.permissions },
      () => !!this.orgInfo?.permissions?.updateOrganizationTarget
    );
  }

  /**
   * Whether the current user can delete the given plan row.
   *
   * Delegates to {@link PlanPermissionsService.canDelete}: ReBAC wins when
   * enforcement is enabled and the backend has hydrated the per-row `canDelete`
   * permission; otherwise the legacy org-level `deleteOrganizationTarget`
   * permission is used via the supplied fallback thunk.
   *
   * ⚠️ The thunk deliberately reads the LEGACY route-org permission, for the
   * same parity reason as {@link canEditPlan}.
   */
  private canDeletePlan(plan: OrgTargetVM): boolean {
    return this.planPermissions.canDelete(
      { targetId: plan.targetId, permissions: plan.permissions },
      () => !!this.orgInfo?.permissions?.deleteOrganizationTarget
    );
  }

  private initActions() {
    this.actions = {
      getMenuConfig: this.initActionOptions.bind(this),
      bodyClasses: 'guts-p-r-2',
    };
  }

  /**
   * initActionOptions
   * @param plan  Plan for which we're creating action options
   * @returns observable of menu items
   */
  private initActionOptions(plan): Observable<readonly MenuViewModel[]> {
    return of([
      {
        title: this.i18n.Core_Edit,
        defaultAction: () => {
          this.openEditModal(plan);
        },
        isHidden: () =>
          !this.canEditPlan(plan) ||
          !this.actionOptionFiltered.includes('editDetails'),
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Clone,
        defaultAction: () => this.clonePlan(plan),
        isHidden: () =>
          !this.actionOptionFiltered.includes('clone') ||
          !this.planPermissions.canClone({
            targetId: plan.targetId,
            targetType: plan.targetType,
          }),
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_MoveToGlobal,
        defaultAction: () => this.moveToGlobal(plan),
        isHidden: () => {
          // We have to be able to move to global from this location...
          if (!this.actionOptionFiltered.includes('moveToGlobal')) {
            return true;
          }

          // ... and the user has to have permission to do so...
          if (!this.authUser.hasChannelAddTargetPermission) {
            return true;
          }

          return this.orgInfo.type !== 'Tenant';
        },
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Delete,
        defaultAction: () => this.deletePlan(plan),
        isHidden: () =>
          !this.canDeletePlan(plan) ||
          plan.targetType === 'Browse' ||
          !this.actionOptionFiltered.includes('delete'),
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_ManageAccess,
        defaultAction: () =>
          this.router.navigate(['/plan', plan.targetId, 'access']),
        // Must match planManageAccessFeatureGuard, which re-checks the same
        // capability on the route — gating on the feature flag alone offered the
        // option for plans the user cannot manage, then bounced them straight back.
        // No separate flag check: canManageAccess is ReBAC-only (legacy fallback
        // `() => false`), so it is already false when enforcement is off.
        isHidden: () =>
          !this.planPermissions.canManageAccess({
            targetId: plan.targetId,
            permissions: plan.permissions,
          }),
        preventRefocus: true,
      },
    ]);
  }

  private buildDataSource() {
    return ({ ordering, paging, term, filterType }: LoadOptions) => {
      return this.sharedTargetService.getOrgTargets({
        organizationId: this.orgId,
        term,
        count: paging.take,
        skip: (paging.page - 1) * paging.take,
        orderBy: ordering.orderBy,
        sortDescending: ordering.isDescending,
        targetTypes: filterType?.join(','),
      });
    };
  }

  private initializeColumns = () => {
    this.columns = [
      {
        label: 'OrgPlans_Title',
        headClasses: 'l_w30',
        canSort: true,
        sortName: OrgPlansColumnIndex.name,
        template: this.titleColumnTemplate,
      } as DataColumn,
      {
        label: 'OrgPlans_Subtitle',
        headClasses: 'l_w20',
        canSort: true,
        sortName: OrgPlansColumnIndex.subtitle,
        template: this.subtitleColumnTemplate,
      } as DataColumn,
      {
        label: 'OrgPlans_Type',
        headClasses: 'l_w10',
        canSort: true,
        sortName: OrgPlansColumnIndex.targetType,
        template: this.targetTypeColumnTemplate,
      } as DataColumn,
      ...(this.hideLegacyAccessActions
        ? []
        : [
            {
              label: 'OrgPlans_Visibility',
              headClasses: 'l_w10',
              canSort: true,
              sortName: OrgPlansColumnIndex.visibility,
              template: this.visibilityColumnTemplate,
            } as DataColumn,
          ]),
      {
        label: 'OrgPlans_FollowerCount',
        canSort: true,
        headClasses: 'l_w10 right-text',
        bodyClasses: 'right-text',
        sortName: OrgPlansColumnIndex.followers,
        template: this.followersColumnTemplate,
      } as DataColumn,
      {
        label: 'Core_Authors',
        headClasses: 'l_w20',
        template: this.authorsColumnTemplate,
      } as DataColumn,
    ];
  };
}
